import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import CategoryFilter from '../../components/ui/CategoryFilter';
import EBookCover from './components/EBookCover';
import PurchaseSection from './components/PurchaseSection';
import EBookInfo from './components/EBookInfo';
import SamplePreview from './components/SamplePreview';


import Icon from '../../components/AppIcon';

const EBookDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [ebook, setEbook] = useState(null);

  const mockEBooks = [
  {
    id: "1",
    title: "The Strategic Execution Framework",
    subtitle: "Transform Strategy into Measurable Results in 90 Days",
    author: "Dr. Sarah Mitchell",
    authorTitle: "Former McKinsey Partner & Strategy Consultant",
    authorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_141b6fda2-1763295319855.png",
    authorImageAlt: "Professional headshot of Dr. Sarah Mitchell, a confident woman with shoulder-length brown hair wearing a navy blazer against a neutral background",
    authorBio: "Dr. Sarah Mitchell has spent 20 years helping Fortune 500 companies execute complex strategies. Her framework has been implemented by over 500 organizations worldwide, resulting in an average 40% improvement in strategic goal achievement. She holds a PhD in Organizational Behavior from Stanford and has authored three bestselling business books.",
    authorCredentials: ["PhD Stanford", "Ex-McKinsey Partner", "500+ Implementations", "3x Bestselling Author"],
    coverImage: "/assets/images/Book_Cover-1772192740288.jpg",
    coverImageAlt: "Mastering SWOT Analysis: A Practical Guide for Success book cover",
    price: 29.99,
    originalPrice: 49.99,
    category: "Business Strategy",
    publishDate: "January 2026",
    pageCount: 247,
    downloadCount: "3,200+",
    rating: 4.8,
    reviewCount: 1247,
    badge: "Bestseller",
    description: "This comprehensive guide provides a proven, step-by-step framework for translating high-level strategic plans into concrete actions that drive measurable business outcomes. Drawing from real-world case studies and 20 years of consulting experience, this eBook eliminates the gap between strategy formulation and execution that plagues 70% of organizations.\n\nYou'll discover the exact methodology used by Fortune 500 companies to achieve strategic goals consistently, including detailed templates, assessment tools, and implementation checklists. Each chapter builds upon practical frameworks that can be immediately applied to your organization, regardless of size or industry.",
    learningPoints: [
    "Master the 5-phase Strategic Execution Framework used by top-performing organizations",
    "Implement proven goal-setting methodologies that increase achievement rates by 40%",
    "Create accountability systems that ensure consistent follow-through on strategic initiatives",
    "Develop communication strategies that align teams around strategic priorities",
    "Build measurement dashboards that track progress and enable data-driven adjustments",
    "Navigate common execution pitfalls with proven mitigation strategies"],

    tableOfContents: [
    {
      title: "The Execution Gap: Why Strategies Fail",
      description: "Understanding the root causes of strategy failure and the cost of poor execution"
    },
    {
      title: "Phase 1: Strategic Clarity & Alignment",
      description: "Establishing clear objectives and ensuring organizational buy-in"
    },
    {
      title: "Phase 2: Action Planning & Resource Allocation",
      description: "Breaking down strategy into executable initiatives with proper resourcing"
    },
    {
      title: "Phase 3: Implementation & Momentum Building",
      description: "Launching initiatives and maintaining execution velocity"
    },
    {
      title: "Phase 4: Monitoring & Course Correction",
      description: "Tracking progress and making data-driven adjustments"
    },
    {
      title: "Phase 5: Sustaining Results & Continuous Improvement",
      description: "Embedding execution excellence into organizational culture"
    },
    {
      title: "Case Studies: Real-World Applications",
      description: "Detailed examples from manufacturing, technology, and service industries"
    },
    {
      title: "Implementation Toolkit",
      description: "Templates, checklists, and assessment tools for immediate application"
    }],

    samplePages: [
    {
      title: "Chapter 1: The Execution Gap",
      content: `Research consistently shows that 70% of strategic initiatives fail not due to poor strategy, but due to inadequate execution. This execution gap costs organizations billions annually in lost opportunities, wasted resources, and competitive disadvantage.\n\nThe root cause isn't lack of effort or commitment. Most organizations have talented people working hard. The problem lies in the absence of a systematic framework that bridges the gap between strategic intent and operational reality.\n\nConsider the case of TechCorp, a mid-sized software company that spent six months developing an ambitious growth strategy. The executive team was aligned, the market opportunity was clear, and the financial projections were compelling. Yet twelve months later, they had achieved only 30% of their strategic objectives.\n\nWhat went wrong? The strategy was sound, but the company lacked:\n• Clear accountability for strategic initiatives\n• Systematic progress tracking mechanisms\n• Effective communication cascading through the organization\n• Resource allocation aligned with strategic priorities\n• A process for addressing obstacles and making adjustments\n\nThis eBook provides the framework TechCorp and hundreds of other organizations have used to transform their execution capabilities.`,
      framework: {
        title: "The 5 Execution Pillars",
        steps: [
        "Clarity: Everyone understands what success looks like",
        "Accountability: Clear ownership for each strategic initiative",
        "Measurement: Data-driven tracking of progress and outcomes",
        "Communication: Regular, structured dialogue about execution",
        "Adaptation: Systematic process for course correction"]

      }
    },
    {
      title: "Chapter 2: Phase 1 - Strategic Clarity",
      content: `Before execution can begin, you must establish absolute clarity about what you're trying to achieve. Vague aspirations like "increase market share" or "improve customer satisfaction" are insufficient. Effective execution requires specific, measurable objectives that everyone can understand and rally behind.\n\nThe Strategic Clarity Framework consists of three components:\n\n1. Outcome Definition: What specific, measurable results will indicate success?\n2. Success Metrics: How will we quantify progress toward these outcomes?\n3. Alignment Verification: How do we ensure everyone interprets objectives consistently?\n\nLet's examine how GlobalManufacturing applied this framework. Their initial strategic objective was "become more innovative." Through the clarity process, they refined this to:\n\n"Launch three new product lines generating $50M in revenue by Q4 2026, with customer satisfaction scores above 4.5/5.0 and development costs under $15M."\n\nNotice the difference. The refined objective is:\n• Specific (three new product lines)\n• Measurable ($50M revenue, 4.5/5.0 satisfaction, $15M costs)\n• Time-bound (Q4 2026)\n• Actionable (teams know what to build)\n\nThis level of clarity eliminates ambiguity and enables effective execution planning.`,
      framework: {
        title: "Clarity Checklist",
        steps: [
        "Can a new employee understand the objective without explanation?",
        "Are success criteria quantifiable and verifiable?",
        "Is the timeline specific and realistic?",
        "Do all stakeholders interpret the objective identically?",
        "Can progress be measured at regular intervals?"]

      }
    },
    {
      title: "Chapter 3: Building Accountability Systems",
      content: `Accountability is the cornerstone of execution excellence. Without clear ownership and consequences, even the best strategies languish. Yet many organizations struggle with accountability because they confuse it with blame or punishment.\n\nEffective accountability is about clarity, support, and follow-through. It answers three questions:\n1. Who is responsible for what?\n2. What support do they need to succeed?\n3. How will we track and discuss progress?\n\nThe Accountability Matrix provides a structured approach:\n\nFor each strategic initiative, identify:\n• The Owner: Single person accountable for results\n• The Team: People who will execute the work\n• The Sponsor: Executive who removes obstacles\n• The Stakeholders: Groups affected by or contributing to success\n\nConsider FinanceServices, a regional bank that struggled with accountability. Multiple people were "responsible" for their digital transformation, which meant no one truly owned it. Progress stalled.\n\nThey implemented the Accountability Matrix, appointing Sarah Chen as the single owner. Sarah had authority to make decisions, access to resources, and executive sponsorship from the COO. Within 90 days, the initiative accelerated dramatically.\n\nKey insight: Shared responsibility often means no responsibility. Effective execution requires single-point accountability.`
    }]

  },
  {
    id: "2",
    title: "Data-Driven Decision Making",
    subtitle: "Leverage Analytics for Competitive Advantage",
    author: "Michael Chen",
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_19c06bd33-1764802011908.png",
    coverImageAlt: "Business analytics book cover with data visualization graphics, charts and graphs in shades of blue and white on a clean modern design",
    price: 34.99,
    originalPrice: 54.99,
    category: "Business Strategy",
    rating: 4.7,
    reviewCount: 892,
    badge: "New Release"
  },
  {
    id: "3",
    title: "Leadership in Crisis",
    subtitle: "Navigate Uncertainty with Confidence",
    author: "Jennifer Rodriguez",
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9d989ed-1770276462035.png",
    coverImageAlt: "Leadership book cover featuring a mountain peak at sunrise symbolizing overcoming challenges, with bold text in gold and navy colors",
    price: 27.99,
    category: "Leadership",
    rating: 4.9,
    reviewCount: 1456
  }];


  const mockTestimonials = [
  {
    id: 1,
    userName: "David Thompson",
    userTitle: "VP of Operations, TechCorp",
    userImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a79b8e72-1763295320816.png",
    userImageAlt: "Professional headshot of David Thompson, a middle-aged man with short gray hair wearing a dark suit and blue tie",
    rating: 5,
    comment: "This framework transformed how we execute strategy. Within 90 days, we saw measurable improvements in goal achievement. The step-by-step approach made implementation straightforward, even for our distributed teams.",
    outcome: "Achieved 85% of strategic goals vs. 30% previously"
  },
  {
    id: 2,
    userName: "Maria Garcia",
    userTitle: "CEO, GlobalManufacturing",
    userImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1725ab708-1763294479697.png",
    userImageAlt: "Professional headshot of Maria Garcia, a confident woman with long dark hair wearing a white blazer against a gray background",
    rating: 5,
    comment: "The accountability systems alone were worth the investment. We finally have clarity on who owns what, and our execution velocity has doubled. The templates and tools are immediately applicable.",
    outcome: "Reduced strategy-to-execution time by 60%"
  },
  {
    id: 3,
    userName: "James Wilson",
    userTitle: "Strategy Director, FinanceServices",
    userImage: "https://img.rocket.new/generatedImages/rocket_gen_img_163a93ce6-1763300379509.png",
    userImageAlt: "Professional headshot of James Wilson, a young professional with short brown hair wearing glasses and a navy suit",
    rating: 5,
    comment: "After reading dozens of strategy books, this is the first one that actually helped us execute. The case studies are relevant, the frameworks are practical, and the results speak for themselves.",
    outcome: "Launched 3 major initiatives on time and under budget"
  },
  {
    id: 4,
    userName: "Lisa Anderson",
    userTitle: "COO, RetailChain",
    userImage: "https://img.rocket.new/generatedImages/rocket_gen_img_16e75c406-1763294340369.png",
    userImageAlt: "Professional headshot of Lisa Anderson, a woman with blonde hair in a bun wearing a burgundy blazer and pearl necklace",
    rating: 4,
    comment: "The measurement dashboards have been game-changing for our leadership team. We now have real-time visibility into strategic progress and can make data-driven adjustments quickly.",
    outcome: "Improved strategic goal visibility by 100%"
  }];


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ebookId = params?.get('id') || '1';

    const selectedEbook = mockEBooks?.find((book) => book?.id === ebookId);
    if (selectedEbook) {
      setEbook(selectedEbook);
    } else {
      navigate('/e-book-catalog');
    }
  }, [location?.search, navigate]);

  if (!ebook) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <CategoryFilter />
        <div className="pt-32 pb-12 px-6">
          <div className="max-w-[1536px] mx-auto flex items-center justify-center">
            <div className="text-center space-y-4">
              <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto" />
              <p className="text-lg text-muted-foreground">Loading eBook details...</p>
            </div>
          </div>
        </div>
      </div>);

  }

  const relatedBooks = mockEBooks?.filter((book) => book?.id !== ebook?.id);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <CategoryFilter />
      <main className="pt-32 pb-12 px-4 md:px-6">
        <div className="max-w-[1536px] mx-auto">
          <button
            onClick={() => navigate('/e-book-catalog')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-6 focus-ring rounded-md px-2 py-1">
            
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Back to Catalog</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Left Column: Cover, Price, Download */}
            <div className="lg:col-span-1 space-y-6">
              <EBookCover ebook={ebook} />
              <PurchaseSection ebook={ebook} />
            </div>

            {/* Right Column: Title, Subtitle, Overview, What You'll Learn, Sample Preview */}
            <div className="lg:col-span-2 space-y-6">
              <EBookInfo ebook={ebook} />
              <SamplePreview ebook={ebook} />
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default EBookDetail;